/**
 * Drift-correction policy: pure decisions, no DOM, no timers.
 *
 * The room owns the timeline. For `playing` the expected media position at an
 * estimated server instant is
 *
 *   expected = anchor_position_ms + (estimated_server_now_ms - anchor_server_ms)
 *
 * clamped to the known duration; for `paused`/`buffering` the fixed anchor
 * position is the expected position. The player's job is to keep the media
 * element near that expectation without producing audible artifacts:
 *
 * - inside the deadband: do nothing;
 * - moderate error: a small, temporary playback-rate nudge inside
 *   [0.97, 1.03], sized to close the error over `rateWindowMs`;
 * - large error, item change, or reconnect: one hard seek, then a cooldown so
 *   a burst of snapshots cannot turn into a burst of seeks;
 * - converged, paused, failed, or unloading: rate back to exactly 1.0.
 *
 * The thresholds below are the tuning defaults from
 * plan/03-synchronized-player.md item 8 — starting points measured in
 * docs/synchronization.md, not proven constants.
 */

import type { PlaybackState } from '../generated';

export const SYNC_TUNING = {
  /** Errors at or below this are ignored entirely. */
  deadbandMs: 100,
  /**
   * Hysteresis: once a rate correction is engaged it stays engaged until the
   * error falls to this, so the rate does not flap on and off at the deadband.
   */
  releaseMs: 40,
  /** Above this error a rate nudge would take too long; seek instead. */
  hardSeekMs: 750,
  /**
   * Time a rate correction is sized to take. 8 s keeps the nudge under the
   * 3 % cap for every error the rate path handles (<=750 ms) and stays far
   * below the pitch-shift range listeners notice.
   */
  rateWindowMs: 8000,
  minRate: 0.97,
  maxRate: 1.03,
  /** No further corrective seek within this window after a seek. */
  seekCooldownMs: 1500,
  /** Correction cadence; 250 ms matches the measurement sample cadence. */
  tickIntervalMs: 250,
  /**
   * A hard seek does not resume instantly: the decoder refills from the
   * nearest keyframe, and the wall time that costs leaves the element behind
   * the room by exactly that much. Measured on this project's progressive MP4s
   * it is ~300-400 ms — just under `hardSeekMs`, so the rate path then needs
   * >10 s to claw it back. Instead of seeking again (audible) the controller
   * seeks slightly *ahead* by a learned lead, and refines the lead from the
   * drift observed `seekObservationDelayMs` after each seek.
   */
  seekObservationDelayMs: 600,
  maxSeekLeadMs: 1500,
  /** Weight of a new observation in the lead's exponential average. */
  seekLeadSmoothing: 0.4,
} as const;

/** What the player should do to the media element right now. */
export type SyncAction =
  /** Leave the element alone (inside deadband, or in a post-seek cooldown). */
  | { readonly kind: 'hold' }
  /** Set `playbackRate` (including back to 1.0 after convergence). */
  | { readonly kind: 'rate'; readonly rate: number }
  /** Set `currentTime` (ms) and reset the rate to 1.0. */
  | { readonly kind: 'seek'; readonly positionMs: number };

export interface SyncInput {
  /** Room position the element should be at, in ms. */
  readonly expectedMs: number;
  /** The element's `currentTime`, in ms. */
  readonly actualMs: number;
  /** The element's current `playbackRate`. */
  readonly currentRate: number;
  /**
   * Bypasses deadband and cooldown: first attach, media generation change,
   * post-reconnect resynchronization, or a room seek.
   */
  readonly force: boolean;
  /** Client monotonic time, for the seek cooldown. */
  readonly nowMs: number;
  /** Client monotonic time of the last seek this policy issued, if any. */
  readonly lastSeekMs: number | null;
}

/**
 * Rate that closes `errorMs` over `rateWindowMs`, clamped to the audible-safe
 * band. `errorMs > 0` means the element is behind the room.
 */
function correctionRate(errorMs: number): number {
  const proportional = 1 + errorMs / SYNC_TUNING.rateWindowMs;
  return Math.min(SYNC_TUNING.maxRate, Math.max(SYNC_TUNING.minRate, proportional));
}

export function decideSync(input: SyncInput): SyncAction {
  const errorMs = input.expectedMs - input.actualMs;
  const magnitude = Math.abs(errorMs);

  if (input.force) {
    return { kind: 'seek', positionMs: input.expectedMs };
  }

  if (magnitude > SYNC_TUNING.hardSeekMs) {
    const cooling =
      input.lastSeekMs !== null &&
      input.nowMs - input.lastSeekMs < SYNC_TUNING.seekCooldownMs;
    // During the cooldown the element is still settling after the previous
    // seek; its `currentTime` is not yet evidence of a new error.
    if (cooling) return { kind: 'hold' };
    return { kind: 'seek', positionMs: input.expectedMs };
  }

  if (magnitude <= SYNC_TUNING.releaseMs) {
    return input.currentRate === 1 ? { kind: 'hold' } : { kind: 'rate', rate: 1 };
  }

  if (magnitude < SYNC_TUNING.deadbandMs) {
    // Inside the deadband: keep an already-engaged correction running (it is
    // converging) but never start a new one.
    return { kind: 'hold' };
  }

  const rate = correctionRate(errorMs);
  return input.currentRate === rate ? { kind: 'hold' } : { kind: 'rate', rate };
}

/** The room's expected media position, or `null` when nothing is loaded. */
export function expectedPositionMs(
  playback: PlaybackState,
  estimatedServerNowMs: number | null,
): number | null {
  switch (playback.state) {
    case 'playing': {
      if (estimatedServerNowMs === null) return null;
      const elapsed = estimatedServerNowMs - playback.anchor_server_ms;
      const position = playback.anchor_position_ms + elapsed;
      return Math.min(Math.max(position, 0), playback.media.duration_ms);
    }
    case 'paused':
    case 'buffering':
      return Math.min(Math.max(playback.position_ms, 0), playback.media.duration_ms);
    case 'idle':
    case 'preparing':
    case 'failed':
      return null;
  }
}
